<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import Currency from '@/components/Currency.vue'
import SportIcon from '@/components/SportIcon.vue'
import BetStatus from '@/components/BetStatus.vue'

const router = useRouter()

// Props
const props = defineProps({
  bet: {
    type: Object,
    required: true
  }
})

// Methods
const getStatusColor = (status) => {
  const colors = {
    pending: '',//warning
    won: 'success',
    lost: 'error',
    void: 'grey'
  }
  return colors[status] || 'grey'
}

const goToRoute = () => {
  let routeName
  let routeParams

  if (props.bet.bet_type === 'combo') {
    routeName = 'mybet'
    routeParams = { id: props.bet.id }

  } else {
    routeName = 'event'
    routeParams = { id: props.bet.event.id }
  }

  return { name: routeName, params: routeParams }
}

// Computed che conta il numero di bet.items con status = 'won'
const countWonBets = computed(() => {
  if (!props.bet.items || props.bet.items.length === 0) {
    return 0
  }
  return props.bet.items.filter(item => item.status === 'won').length
})

const countLostBets = computed(() => {
  if (!props.bet.items || props.bet.items.length === 0) {
    return 0
  }
  return props.bet.items.filter(item => item.status === 'lost').length
})

</script>

<template>
  <v-card 
    class="mb-4 pt-1 bet-card position-relative" 
    elevation="3"
    hover
    rounded="lg"
    tag="a"
    :to="goToRoute()"
  >
    <!-- Status badge -->
    <v-chip 
      :color="getStatusColor(bet.status)" 
      size="small" 
      class="status-chip"
      variant="tonal"
    >
      <v-icon v-if="bet.bet_type == 'combo'">mdi-shape-outline</v-icon>
      <SportIcon v-else
        :sport-key="bet.event.sport_key || 'soccer'" 
        size="small"
      />
    </v-chip>

    <v-card-title class="pb-2">
      
      <div v-if="bet.bet_type == 'combo'" class="betlist-teams">
        {{ $t('events.betting.betTypes.combo') }}
      </div>
      <div v-else class="betlist-teams d-flex flex-column flex-sm-row align-center text-h6">
        <span class="text-truncate">{{ bet.event.home_team }}</span>
        <span class="vs-separator mx-2">vs</span>
        <span class="text-truncate">{{ bet.event.away_team }}</span>
      </div>

      <div v-if="bet.display_time" class="text-center text-sm-start text-caption text-medium-emphasis py-3">
        <v-icon size="small" class="me-1">mdi-calendar</v-icon>
        {{ $d(bet.display_time, 'long') }}
      </div>
    </v-card-title>
    
    <v-card-text class="pt-0">
      <!-- Dettagli scommessa con icone -->
      <v-row class="bet-details" no-gutters>
        <v-col cols="12" class="text-center text-sm-start mb-3">
          <v-chip 
            v-if="bet.bet_type != 'combo'"
            color="surface-variant" 
            size="small" 
            variant="tonal"
            class="me-2 mb-2"
          >
            {{ $t('events.betting.betTypes.' + bet.bet_type) }}
            
          </v-chip>
          <v-chip 
            v-if="bet.bet_type != 'combo'"
            color="info" 
            size="small" 
            variant="tonal"
            class="me-2 mb-2"
          >
            <v-icon start size="small">mdi-target</v-icon>
            {{ bet.selection }}
          </v-chip>
          <v-chip 
            color="warning" 
            size="small" 
            variant="tonal"
            class="me-2 mb-2"
          >
            <v-icon start size="small">mdi-chart-line</v-icon>
            {{ bet.odds }}
          </v-chip>

          <v-chip v-if="bet.items && bet.items.length > 0"
            color="primary" 
            size="small" 
            variant="tonal"
            class="me-2 mb-2"
          >
            {{ $t('combo.n_events', { n: bet.items.length }) }}
          </v-chip>

          <v-chip v-if="countLostBets"
            color="error" 
            size="small" 
            variant="tonal"
            class="me-2 mb-2"
          >
            <v-icon start size="small">mdi-close</v-icon>
            {{ countLostBets }}
          </v-chip>

          <v-chip v-if="countWonBets"
            color="success" 
            size="small" 
            variant="tonal"
            class="me-2 mb-2"
          > 
            <v-icon start size="small">mdi-trophy</v-icon>
            {{ countWonBets }}
          </v-chip>
        </v-col>
      </v-row>

      <!-- Importi -->
      <v-card 
        variant="tonal" 
        color="surface" 
        class="pa-3 mb-3"
        rounded="lg"
      >
        <v-row no-gutters>
          <v-col cols="6">
            <div class="text-center">
              <div class="text-caption text-medium-emphasis mb-1">
                {{ $t('myBets.details.amountBet') }}
              </div>
              <div class="text-h6 text-primary font-weight-bold">
                <Currency :amount="bet.amount" />
              </div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="text-center">
              <div class="text-caption text-medium-emphasis mb-1">
                {{ bet.status === 'won' ? $t('myBets.details.actualWin') : $t('myBets.details.potentialWin') }}
              </div>
              <div class="text-h6 font-weight-bold" :class="{
                'text-success': bet.status === 'won',
                'text-secondary': bet.status !== 'won'
              }">
                <Currency :amount="bet.status === 'won' ? bet.payout - bet.amount : (bet.amount * bet.odds) - bet.amount" />
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>
      
      <!-- Risultato finale -->
      <BetStatus :status="bet.status" :withAlert="true" />

    </v-card-text>
  </v-card>
</template>


<style scoped>
.betlist-teams {
  gap: 7px;
}
.status-chip {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  font-weight: 600;
}

.vs-separator {
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-weight: 500;
  font-size: 0.9em;
}


.v-chip {
  transition: transform 0.2s ease;
}


/* Miglioramenti responsive */
@media (max-width: 768px) {
  .betlist-teams {
    gap: 0;
  }
}

</style>